/**
 * The Machines table: name and owner, tags as quiet chips, the
 * overlay address with its mesh name, the agent's version against the newest the portal holds (the
 * platform's own gateway and relay run the platform's version and update with it), OS,
 * last seen and key expiry; search, a filter popover and a CSV export; one menu per row.
 *
 * A list group (Layout.tsx) whose search, filter and export sit in its heading, with the table as a
 * `.data-table` flush in the card; a column that matters less hides as the group narrows.
 */

import type { MeshNode } from '@aioe/shared';
import { ArrowDownToLine, Funnel } from '@gravity-ui/icons';
import { Button, Popover, Sheet, Tooltip } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { listCount, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { timeAgo } from '../hooks.ts';
import { NodeActivity } from './meshLogs.tsx';
import { OFF_REASON } from './meshWords.ts';
import { ChoiceSelect, ConfirmDialog, ErrorNote, inputClass, ListBlock, ListGroup, StatusMark, Tick } from './ui.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { SearchBox } from './ui/SearchBox.tsx';

const DAY = 86_400_000;

/** True when `a` is an older version than `b` (dotted numbers, a leading v ignored). */
export function olderVersion(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  const p = (v: string) => v.replace(/^v/, '').split(/[.-]/).map((x) => Number.parseInt(x, 10) || 0);
  const x = p(a);
  const y = p(b);
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (x[i] ?? 0) - (y[i] ?? 0);
    if (d !== 0) return d < 0;
  }
  return false;
}

const OS_NAME: Record<string, string> = { windows: 'Windows', linux: 'Linux', darwin: 'macOS' };
const KIND_NAME: Record<MeshNode['kind'], string> = { workbench: 'Workbench', node: 'Headless node', mobile: 'Phone', gateway: 'Gateway', relay: 'Relay' };

function expiryOf(n: MeshNode, now = Date.now()): { text: string; expired: boolean; soon: boolean } {
  if (!n.keyExpiresAt) return { text: 'Never', expired: false, soon: false };
  const ms = Date.parse(n.keyExpiresAt) - now;
  if (ms <= 0) return { text: 'Expired', expired: true, soon: false };
  const d = Math.ceil(ms / DAY);
  return { text: d === 1 ? 'Tomorrow' : `In ${d} days`, expired: false, soon: d <= 14 };
}

export interface MachineContext {
  /** Owner names by user id (from the directory the mesh keeps). */
  names: ReadonlyMap<string, string>;
  /** The platform of the fleet device behind a node, by device id. */
  platforms: ReadonlyMap<string, string>;
  /** The newest mesh agent the portal holds. */
  latestAgent?: string;
}

/** The platform's own gateway and relay: they run the platform's version and update with it, never as a mesh agent. */
export const platformNode = (n: Pick<MeshNode, 'kind'>) => n.kind === 'gateway' || n.kind === 'relay';
/** Whether a newer mesh agent is there for this machine; never for the platform's own nodes. */
export const meshAgentUpdate = (n: Pick<MeshNode, 'kind' | 'agentVersion'>, latest: string | undefined) => !platformNode(n) && olderVersion(n.agentVersion, latest);

const ownerOf = (n: MeshNode, ctx: MachineContext) => ctx.names.get(n.ownerUserId) ?? n.ownerEmail ?? (platformNode(n) ? 'The platform' : n.ownerUserId);
const osOf = (n: MeshNode, ctx: MachineContext) => (n.deviceId ? OS_NAME[ctx.platforms.get(n.deviceId) ?? ''] : undefined);
const statusOf = (n: MeshNode) => (n.disabled ? 'off' : expiryOf(n).expired ? 'expired' : n.online ? 'online' : 'offline');

interface Filters {
  kind?: MeshNode['kind'];
  status?: 'online' | 'offline' | 'off';
  update?: boolean;
  expiring?: boolean;
}

function csvOf(rows: readonly MeshNode[], ctx: MachineContext): string {
  const q = (v: string | undefined) => `"${(v ?? '').replace(/"/g, '""')}"`;
  const head = ['Name', 'Owner', 'Kind', 'Tags', 'Address', 'Mesh name', 'Agent version', 'OS', 'Status', 'Last seen', 'Key expires'];
  const body = rows.map((n) => [n.name, ownerOf(n, ctx), KIND_NAME[n.kind], n.tags.join(' '), n.address, n.dnsName, n.agentVersion, osOf(n, ctx), statusOf(n), n.lastSeenAt, n.keyExpiresAt ?? 'never'].map(q).join(','));
  return [head.map(q).join(','), ...body].join('\r\n');
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export type Action = { kind: 'rename' | 'tags' | 'activity' | 'remove' | 'rotate' | 'expiry'; node: MeshNode };

export function MeshMachines({ ctx, rev, isAdmin, offAllowed, selected, onSelect, onChanged }: { ctx: MachineContext; rev: number; isAdmin: boolean; offAllowed: boolean; selected?: string; onSelect: (id: string) => void; onChanged: () => void }) {
  const search = useListSearch();
  const [f, setF] = useState<Filters>({});
  const [action, setAction] = useState<Action>();
  const active = Object.values(f).filter(Boolean).length;
  // Searched and filtered on the server, a page at a time (the update filter asks for agents older than the newest the portal holds).
  const query = { q: search.q, kind: f.kind, status: f.status, update: f.update && ctx.latestAgent ? ctx.latestAgent : undefined, expiring: f.expiring, rev };
  const list = usePagedList((cursor) => routes.meshNodesPage({ q: search.q, kind: f.kind, status: f.status, ...(f.update && ctx.latestAgent ? { olderThan: ctx.latestAgent } : {}), expiring: f.expiring, cursor }), query, { intervalMs: 15_000, keyOf: (n) => n.id });
  const rows = list.items;
  const narrowed = search.q !== '' || active > 0;
  const setQ = search.setTyped;

  return (
    <>
      <ListGroup
        title="Machines"
        count={list.loaded ? listCount(rows.length, { one: 'machine', many: 'machines' }, { total: narrowed ? undefined : list.extra?.total, hasMore: list.hasMore, searched: narrowed }) : undefined}
        description="Choose one to see who it can reach."
        actions={
          <>
            <SearchBox label="Search machines" value={search.typed} onChange={setQ} placeholder="Search machines" className="w-40 max-w-full @lg:w-56" />
            <Popover>
              <Button size="sm" variant="secondary" aria-label="Filter machines">
                <Funnel className="size-4" />
                Filter{active ? ` (${active})` : ''}
              </Button>
              <Popover.Content placement="bottom end" className="w-72 max-w-[calc(100vw-1rem)]">
                <Popover.Dialog className="grid gap-3 p-3" aria-label="Filter machines">
                  <ChoiceSelect label="Kind" placeholder="Any kind" value={f.kind} options={KIND_NAME} onChange={(kind) => setF({ ...f, kind })} />
                  <ChoiceSelect label="Status" placeholder="Any status" value={f.status} options={{ online: 'Online', offline: 'Offline', off: 'Off the mesh' }} onChange={(status) => setF({ ...f, status })} />
                  <Tick checked={!!f.update} onChange={(update) => setF({ ...f, update })}>
                    Update available
                  </Tick>
                  <Tick checked={!!f.expiring} onChange={(expiring) => setF({ ...f, expiring })}>
                    Key expires within 14 days
                  </Tick>
                  {active > 0 && (
                    <Button size="sm" variant="ghost" onPress={() => setF({})}>
                      Clear filters
                    </Button>
                  )}
                </Popover.Dialog>
              </Popover.Content>
            </Popover>
            <Button size="sm" variant="secondary" aria-label="Export the machines shown as CSV" isDisabled={rows.length === 0} onPress={() => download('mesh-machines.csv', csvOf(rows, ctx))}>
              <ArrowDownToLine className="size-4" />
              CSV
            </Button>
          </>
        }
      >
        {rows.length === 0 ? (
          <ListBlock className="py-6 text-center text-sm text-muted">
            {!list.loaded ? 'Asking the mesh…' : narrowed ? (
              <>
                No machine matches{search.q ? ` "${search.q}"` : ' the filters'}.{' '}
                <Button size="sm" variant="ghost" onPress={() => (search.clear(), setF({}))}>
                  Clear
                </Button>
              </>
            ) : 'No machines yet.'}
          </ListBlock>
        ) : (
          <ListBlock flush>
            <div className="data-table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Machine</th>
                    <th className="hidden w-44 @2xl:table-cell">Address</th>
                    <th className="hidden w-36 @4xl:table-cell">Version</th>
                    <th className="w-32 @2xl:w-40">Status</th>
                    <th className="hidden w-32 @5xl:table-cell">Key expires</th>
                    {isAdmin && <th className="w-14" aria-label="Actions" />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((n) => {
                    const ex = expiryOf(n);
                    const os = osOf(n, ctx);
                    const update = meshAgentUpdate(n, ctx.latestAgent);
                    return (
                      <tr key={n.id} data-pressable className={selected === n.id ? 'bg-surface-secondary' : ''} onClick={() => onSelect(n.id)}>
                        <td>
                          <button type="button" className="data-table__open block max-w-full truncate font-medium" onClick={(e) => (e.stopPropagation(), onSelect(n.id))}>
                            {n.name}
                          </button>
                          <span className="data-table__meta truncate">
                            {ownerOf(n, ctx)} · {KIND_NAME[n.kind]}
                            {os ? ` · ${os}` : ''}
                          </span>
                          {(n.tags.length > 0 || (n.requestedTags ?? []).some((t) => !n.tags.includes(t))) && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {n.tags.map((t) => (
                                <span key={t} className="mono rounded-md bg-surface-secondary px-1.5 py-0.5 text-xs text-muted">
                                  {t}
                                </span>
                              ))}
                              {(n.requestedTags ?? [])
                                .filter((t) => !n.tags.includes(t))
                                .map((t) => (
                                  <Tooltip key={t} delay={300}>
                                    <Tooltip.Trigger>
                                      <span tabIndex={0} className="rounded-md border border-dashed border-separator px-1.5 py-0.5 text-xs text-muted">
                                        asked for: <span className="mono">{t}</span>
                                      </span>
                                    </Tooltip.Trigger>
                                    <Tooltip.Content className="max-w-64">{isAdmin ? `This machine asked for the ${t} tag. It counts in the access rules once you give it the tag: Edit tags in the row's menu.` : `This machine asked for the ${t} tag. It counts in the access rules once an administrator gives it the tag.`}</Tooltip.Content>
                                  </Tooltip>
                                ))}
                            </div>
                          )}
                        </td>
                        <td className="hidden @2xl:table-cell" onClick={(e) => e.stopPropagation()}>
                          <Popover>
                            <Button size="sm" variant="ghost" className="mono -ml-2 h-6 px-2 text-xs" aria-label={`Names for ${n.name}`}>
                              {n.address}
                            </Button>
                            <Popover.Content placement="bottom start" className="w-80 max-w-[calc(100vw-1rem)]">
                              <Popover.Dialog className="grid gap-2 p-3 text-sm" aria-label={`Names for ${n.name}`}>
                                <span className="pane-label">Mesh name</span>
                                <span className="mono break-all">{n.dnsName ?? 'None yet'}</span>
                                <span className="pane-label">Address</span>
                                <span className="mono">{n.address}</span>
                                <span className="text-xs text-muted">Names resolve only inside the mesh.</span>
                                {n.dnsName && (
                                  <Button size="sm" variant="secondary" onPress={() => void navigator.clipboard?.writeText(n.dnsName!)}>
                                    Copy the name
                                  </Button>
                                )}
                              </Popover.Dialog>
                            </Popover.Content>
                          </Popover>
                        </td>
                        <td className="hidden text-xs @4xl:table-cell">
                          <span className="mono block truncate">{n.agentVersion ?? '—'}</span>
                          {platformNode(n) ? <span className="block text-muted">Updates with the platform</span> : update && <span className="block text-warning">Update available: {ctx.latestAgent}</span>}
                        </td>
                        <td>
                          {n.disabled ? <StatusMark state="disabled" label="Off the mesh" /> : ex.expired ? <StatusMark state="error" label="Key expired" /> : <StatusMark state={n.online ? 'online' : 'offline'} />}
                          <span className="data-table__meta truncate">{n.disabled && n.disabledReason ? OFF_REASON[n.disabledReason] : n.online ? '' : timeAgo(n.lastSeenAt)}</span>
                        </td>
                        <td className={`hidden text-xs @5xl:table-cell ${ex.expired || ex.soon ? 'text-warning' : 'text-muted'}`}>
                          {ex.text}
                          {n.keyExpiryDisabled && <span className="block text-muted">Turned off</span>}
                        </td>
                        {isAdmin && (
                          <td className="data-table__end py-1.5" onClick={(e) => e.stopPropagation()}>
                            <RowMenu
                              label={`More for ${n.name}`}
                              items={[
                                { id: 'rename', label: 'Rename…' },
                                { id: 'tags', label: 'Edit tags…' },
                                { id: 'expiry', label: n.keyExpiryDisabled ? 'Turn key expiry on' : 'Turn key expiry off', disabled: !n.keyExpiryDisabled && !offAllowed },
                                { id: 'activity', label: 'View recent activity' },
                                { id: 'rotate', label: 'Rotate key now…' },
                                { id: 'remove', label: 'Remove…', danger: true },
                              ]}
                              onAction={(k) => setAction({ kind: k as Action['kind'], node: n })}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </ListBlock>
        )}
        {list.hasMore ? <ShowMore onPress={list.showMore} pending={list.loadingMore} /> : null}
      </ListGroup>
      <MachineActions action={action} onClose={() => setAction(undefined)} onChanged={onChanged} onRemoved={() => onSelect('')} />
    </>
  );
}

export function MachineActions({ action, onClose, onChanged, onRemoved }: { action?: Action; onClose: () => void; onChanged: () => void; onRemoved: () => void }) {
  const [error, setError] = useState<string>();
  const n = action?.node;
  const run = async (f: () => Promise<unknown>, after?: () => void) => {
    setError(undefined);
    try {
      await f();
      onChanged();
      after?.();
      onClose();
    } catch (e) {
      setError(problemText(e));
    }
  };
  if (!n || !action) return null;
  const close = () => (setError(undefined), onClose());
  if (action.kind === 'rename' || action.kind === 'tags' || action.kind === 'activity')
    return (
      <Sheet isOpen onOpenChange={(o) => !o && close()} placement="right">
        <Sheet.Backdrop>
          <Sheet.Content className="w-full max-w-md">
            <Sheet.Dialog aria-label={action.kind === 'rename' ? 'Rename' : action.kind === 'tags' ? 'Edit tags' : 'Recent activity'}>
              <Sheet.Header>
                <Sheet.Heading>{action.kind === 'rename' ? `Rename ${n.name}` : action.kind === 'tags' ? `Tags of ${n.name}` : `Recent activity of ${n.name}`}</Sheet.Heading>
              </Sheet.Header>
              <Sheet.Body className="flex flex-col gap-3 overflow-y-auto">
                {action.kind === 'activity' ? <NodeActivity nodeId={n.id} /> : <EditForm key={action.kind + n.id} kind={action.kind} node={n} error={error} onSave={(f) => void run(f)} onCancel={close} />}
              </Sheet.Body>
              <Sheet.CloseTrigger />
            </Sheet.Dialog>
          </Sheet.Content>
        </Sheet.Backdrop>
      </Sheet>
    );
  if (action.kind === 'remove')
    return (
      <ConfirmDialog isOpen title={`Remove ${n.name} from the mesh?`} confirmLabel="Remove" danger onCancel={close} onConfirm={() => run(() => routes.deleteMeshNode(n.id), onRemoved)}>
        <p>Its peers stop reaching it at once, and its key is retired for good: the machine comes back only with a new key, when its mesh agent joins again.</p>
        <ErrorNote error={error} />
      </ConfirmDialog>
    );
  if (action.kind === 'rotate')
    return (
      <ConfirmDialog isOpen title={`Rotate the key of ${n.name} now?`} confirmLabel="Rotate" onCancel={close} onConfirm={() => run(() => routes.expireMeshNodeKey(n.id))}>
        <p>Its current key expires now. Its mesh agent fetches a new one on its next check and rejoins; until then the machine is off the mesh. The new key lasts the policy's lifetime.</p>
        <ErrorNote error={error} />
      </ConfirmDialog>
    );
  return (
    <ConfirmDialog isOpen title={n.keyExpiryDisabled ? `Turn key expiry on for ${n.name}?` : `Turn key expiry off for ${n.name}?`} confirmLabel={n.keyExpiryDisabled ? 'Turn on' : 'Turn off'} onCancel={close} onConfirm={() => run(() => routes.setMeshNodeKeyExpiry(n.id, n.keyExpiryDisabled ? 'default' : 'never'))}>
      <p>{n.keyExpiryDisabled ? "Its key lasts the policy's lifetime again, counted from when it was issued." : 'Its key never expires, so a server cannot drop off the mesh by surprise. The change is recorded in the configuration log.'}</p>
      <ErrorNote error={error} />
    </ConfirmDialog>
  );
}

function EditForm({ kind, node, error, onSave, onCancel }: { kind: 'rename' | 'tags'; node: MeshNode; error?: string; onSave: (f: () => Promise<unknown>) => void; onCancel: () => void }) {
  const [name, setName] = useState(node.name);
  const [tags, setTags] = useState(node.tags.join(', '));
  const parsed = tags
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((t) => t.replace(/^tag:/, ''));
  const ok = kind === 'rename' ? !!name.trim() && name.trim() !== node.name : parsed.every((t) => /^[a-z0-9][a-z0-9-]{0,39}$/.test(t));
  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok) onSave(() => (kind === 'rename' ? routes.renameMeshNode(node.id, name.trim()) : routes.setMeshNodeTags(node.id, parsed)));
      }}
    >
      {kind === 'rename' ? (
        <>
          <input className={inputClass} aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <p className="text-xs text-muted">Its name on the mesh changes with it; peers find it by the new name. Names resolve only inside the mesh.</p>
        </>
      ) : (
        <>
          <input className={`${inputClass} mono`} aria-label="Tags" placeholder="build, gpu" value={tags} onChange={(e) => setTags(e.target.value)} autoFocus />
          <p className="text-xs text-muted">Separate tags with commas. Tags are what the access rules pick machines by; the ones listed here replace the machine's tags.</p>
        </>
      )}
      <ErrorNote error={error} />
      <div className="flex gap-2">
        <Button type="submit" isDisabled={!ok}>
          Save
        </Button>
        <Button variant="tertiary" onPress={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
